import { useTranslation } from "react-i18next";
import { useClients } from "../api/queries.js";

/**
 * The one client-filter chip row used on Payments, Calendar and Reports
 * (SundialClientFilter.dc.html). "All clients" + one chip per active client;
 * the selected one is orange-filled with a check, never colour-only. Renders
 * NOTHING when there is a single client, so the common freelancer never sees a
 * dead control. Filtering is per-screen; tax figures stay person-level.
 */
export function ClientFilter({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { t } = useTranslation();
  const { data } = useClients();
  const clients = (data?.clients ?? []).filter((c) => !c.archived);
  if (clients.length <= 1) return null;

  const chips = [{ id: "", label: t("clientFilter.all") }, ...clients.map((c) => ({ id: c.id, label: c.name }))];

  const chipStyle = (active: boolean): React.CSSProperties => ({
    flex: "none",
    height: 40,
    padding: "0 15px",
    borderRadius: 999,
    cursor: "pointer",
    font: "600 13px Sora",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: ".14s",
    border: active ? "1px solid var(--primary)" : "1px solid var(--border)",
    background: active ? "var(--primary)" : "var(--card)",
    color: active ? "#fff" : "var(--muted)",
    boxShadow: active ? "0 2px 9px var(--glow)" : "none",
  });

  return (
    <div
      role="tablist"
      aria-label={t("clientFilter.aria")}
      style={{ display: "flex", gap: 8, overflowX: "auto", padding: "2px 0 6px", scrollbarWidth: "none" }}
    >
      {chips.map((ch) => {
        const active = value === ch.id;
        return (
          <button key={ch.id || "all"} role="tab" aria-selected={active} onClick={() => onChange(ch.id)} style={chipStyle(active)}>
            {active && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12l4 4 10-10" />
              </svg>
            )}
            {ch.label}
          </button>
        );
      })}
    </div>
  );
}
